<template>
	<v-navigation-drawer
		v-model="isOpen"
		location="left"
		temporary
		width="500"
		class="trial-edit-modal"
		data-cy="trial-edit-modal"
	>
		<v-card flat height="100%" class="d-flex flex-column">
			<v-card-title class="d-flex flex-column align-start pa-4 flex-shrink-0">
				<div class="d-flex align-center justify-space-between w-100">
					<span class="text-h6">Edit Trial</span>
					<v-btn icon variant="text" size="small" @click="close" data-cy="close-trial-edit-btn">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</div>
				<div
					v-if="editedTrial.trial_id != null || editedTrial.created_tstamp"
					class="text-caption text-medium-emphasis mt-1"
				>
					<span v-if="editedTrial.trial_id != null" data-cy="trial-id-header">
						Trial #{{ editedTrial.trial_id }}
					</span>
					<template v-if="editedTrial.trial_id != null && editedTrial.created_tstamp">
						<span> · </span>
					</template>
					<span v-if="editedTrial.created_tstamp" data-cy="trial-created-date-header">
						Created {{ editedTrial.created_tstamp }}
					</span>
				</div>
			</v-card-title>

			<v-divider class="flex-shrink-0" />

			<v-card-text class="pa-4 flex-grow-1" style="overflow-y: auto">
				<v-form ref="form" v-model="valid" lazy-validation>
					<v-row>
						<v-col cols="12">
							<v-text-field
								v-model="editedTrial.school_name"
								label="School Name*"
								:rules="[rules.required]"
								required
								data-cy="trial-school-name"
							/>
						</v-col>

						<v-col cols="12">
							<v-text-field
								v-model="editedTrial.district_name"
								label="District Name*"
								:rules="[rules.required]"
								required
								data-cy="trial-district-name"
							/>
						</v-col>

						<v-col cols="12">
							<v-text-field
								v-model="editedTrial.address1"
								label="Address 1"
								data-cy="trial-address1"
							/>
						</v-col>

						<v-col cols="12">
							<v-text-field
								v-model="editedTrial.address2"
								label="Address 2"
								data-cy="trial-address2"
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model="editedTrial.city"
								label="City"
								data-cy="trial-city"
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model="editedTrial.state"
								label="State"
								data-cy="trial-state"
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model="editedTrial.zipcode"
								label="Zipcode"
								data-cy="trial-zipcode"
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model="editedTrial.country"
								label="Country"
								data-cy="trial-country"
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model="editedTrial.firstname"
								label="First Name*"
								:rules="[rules.required]"
								required
								data-cy="trial-first-name"
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model="editedTrial.lastname"
								label="Last Name*"
								:rules="[rules.required]"
								required
								data-cy="trial-last-name"
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model="editedTrial.email"
								label="Email*"
								:rules="[rules.required, rules.email]"
								required
								data-cy="trial-email"
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model="editedTrial.phone_number"
								label="Phone Number*"
								:rules="[rules.required]"
								required
								data-cy="trial-phone-number"
							/>
						</v-col>

						<v-col cols="6">
							<v-select
								v-model="editedTrial.timezone"
								label="Timezone*"
								:items="timezones"
								:rules="[rules.required]"
								required
								data-cy="trial-timezone"
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model.number="editedTrial.num_students"
								label="Student Enrollment"
								type="number"
								:rules="[rules.number]"
								data-cy="trial-student-enrollment"
							/>
						</v-col>

						<!-- Show fulfillment status if trial is fulfilled -->
						<v-col cols="12" v-if="props.trial?.status === 'fullfilled'">
							<v-alert 
								:type="props.trial?.status === 'fullfilled' ? 'success' : 'warning'"
								density="compact"
							>
								<template v-if="props.trial?.status === 'fullfilled'">
									<v-icon>mdi-check-circle</v-icon>
									Trial is fully fulfilled - matching school record exists
								</template>
								<template v-else>
									<v-icon>mdi-alert</v-icon>
									Trial status is fulfilled but no matching school record found
								</template>
							</v-alert>
						</v-col>
					</v-row>
				</v-form>
			</v-card-text>

			<v-divider class="flex-shrink-0" />

			<v-card-actions class="pa-4 flex-shrink-0" style="min-height: 80px">
				<v-spacer />
				<v-btn
					color="grey"
					variant="outlined"
					@click="close"
					:disabled="loading"
					data-cy="cancel-trial-edit-btn"
				>
					Cancel
				</v-btn>

				<v-btn
					color="primary"
					variant="flat"
					@click="saveTrial"
					:loading="loading"
					:disabled="!valid"
					data-cy="save-trial-details-btn"
				>
					Save Details
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-navigation-drawer>
</template>

<script setup lang="ts">

import { ref, watch, computed } from 'vue';
import { useAlertsStore } from '@/stores/alerts';
import useTrials, { type TrialMembership } from '@/stores/support/trials';
import dayjs from 'dayjs';

const props = defineProps<{
  modelValue: boolean;
  trial: TrialMembership | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const trialsStore = useTrials();
const alertStore = useAlertsStore();

const form = ref();
const valid = ref(false);
const loading = ref(false);

const editedTrial = ref<Partial<TrialMembership>>({});

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

// US Timezones
const timezones = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu'
];

const rules = {
  required: (value: any) => !!value || 'Field is required',
  email: (value: string) => /.+@.+/.test(value) || 'Invalid email address',
  number: (value: any) => !value || (!isNaN(parseFloat(value)) && isFinite(value)) || 'Must be a number'
};


// Watch for trial prop changes
watch(() => props.trial, (newTrial) => {
  if (newTrial) {
    editedTrial.value = { ...newTrial };
    // Format the date for display
    if (editedTrial.value.created_tstamp) {
      editedTrial.value.created_tstamp = dayjs(editedTrial.value.created_tstamp).format('MM/DD/YYYY HH:mm');
    }
  }
}, { immediate: true });

const close = () => {
  isOpen.value = false;
  // Reset form after animation completes
  setTimeout(() => {
    editedTrial.value = {};
    if (form.value) {
      form.value.resetValidation();
    }
  }, 300);
};

const saveTrial = async () => {
	if (!form.value) return;

	const { valid: isValid } = await form.value.validate();
	if (!isValid || !props.trial) return;

	loading.value = true;

	try {
		// Prepare the update payload from the form data
		const updateData = {
			school_name: editedTrial.value.school_name,
			district_name: editedTrial.value.district_name,
			address1: editedTrial.value.address1,
			address2: editedTrial.value.address2,
			city: editedTrial.value.city,
			state: editedTrial.value.state,
			zipcode: editedTrial.value.zipcode,
			country: editedTrial.value.country,
			firstname: editedTrial.value.firstname,
			lastname: editedTrial.value.lastname,
			email: editedTrial.value.email,
			phone_number: editedTrial.value.phone_number,
			username: editedTrial.value.username,
			timezone: editedTrial.value.timezone,
			num_students: editedTrial.value.num_students
		};

		console.log('Update payload:', updateData);

		const result = await trialsStore.updateTrial(props.trial.trial_id, updateData);

		if (result.status) {
			alertStore.addAlert('success', 'Trial updated successfully', 5);
			console.log('Update completed with payload:', updateData);
			console.log('Updated trial:', result.data);
			close();
		} else {
			alertStore.addAlert('error', result.message || 'Failed to update trial', 10);
		}
	} catch (error) {
		console.error('Error updating trial:', error);
		alertStore.addAlert('error', 'An error occurred while updating the trial', 10);
	} finally {
		loading.value = false;
	}
};
</script>

<style scoped lang="scss">
.trial-edit-modal {
	.v-card-title {
		background-color: #f5f5f5;
		border-bottom: 1px solid #e0e0e0;
	}

	.v-card-actions {
		background-color: #f5f5f5;
		border-top: 1px solid #e0e0e0;
	}
}
</style>
